import { useState, useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import styles from './SearchableSelect.module.css';

/**
 * Selector con búsqueda para listas que YA están cargadas en la página (torres, asambleas,
 * compañías, eventos, administradores…). Reemplaza al <select> nativo cuando la lista puede
 * crecer. Para conjuntos (que se buscan en el servidor) usa PropertyCombobox.
 *  - Se escribe para filtrar (sin distinguir mayúsculas ni tildes) por el texto y el subtítulo.
 *  - Teclado: ↑ ↓ Inicio Fin, Enter elige, Esc cierra, Tab sale. ARIA combobox/listbox.
 *  - El panel se dibuja en <body> (portal) con posición fija: ninguna tarjeta ni modal lo tapa.
 * Props:
 *   value        — valor elegido ('' = ninguno)
 *   onChange     — fn(valor: string, opcion: object|null)
 *   options      — [{ value, label, sub? }]
 *   placeholder  — texto cuando no hay selección (y de la fila «vacía» que limpia)
 *   allowEmpty   — ofrece la fila vacía para limpiar la selección (por defecto sí)
 *   searchable   — muestra la caja de búsqueda; por defecto solo si hay más de 8 opciones (listas cortas = menú limpio)
 *   disabled, minWidth, maxWidth, ariaLabel, searchPlaceholder, triggerClassName (clase del botón, p. ej. tamaño compacto), auto (ancho según contenido)
 */

const MAX_FILAS = 100;
const normalizar = (t) => String(t ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export default function SearchableSelect({
  value, onChange, options = [], placeholder = '— Selecciona —', allowEmpty = true,
  disabled = false, minWidth = 200, maxWidth = 320, ariaLabel = 'Lista', searchPlaceholder = 'Buscar…',
  searchable, triggerClassName, auto = false,
}) {
  const conBusqueda = searchable ?? options.length > 8;
  const listId = useId();
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto]     = useState('');
  const [activo, setActivo]   = useState(0);
  const [pos, setPos]         = useState(null);
  const raiz = useRef(null);
  const entrada = useRef(null);
  const boton = useRef(null);
  const lista = useRef(null);
  const panel = useRef(null);

  const buscado = normalizar(texto.trim());
  const coinciden = buscado
    ? options.filter(o => normalizar(`${o.label} ${o.sub || ''}`).includes(buscado))
    : options;
  const visibles = coinciden.slice(0, MAX_FILAS);
  const conVacia = allowEmpty && !buscado;
  const filas = conVacia ? [{ value: '', label: placeholder, vacio: true }, ...visibles] : visibles;
  const idOpcion = (i) => `${listId}-op-${i}`;
  const elegida = options.find(o => String(o.value) === String(value));
  const esElegida = (o) => (o.vacio ? !value : String(o.value) === String(value));

  // Posición del panel junto al botón (se abre hacia arriba si abajo no hay espacio).
  useEffect(() => {
    if (!abierto) return undefined;
    const calcular = () => {
      const r = boton.current?.getBoundingClientRect();
      if (!r) return;
      const ancho = Math.min(Math.max(r.width, 300), window.innerWidth - 16);
      const izquierda = Math.max(8, Math.min(r.left, window.innerWidth - ancho - 8));
      const abajo = window.innerHeight - r.bottom;
      const sube = abajo < 390 && r.top > abajo;
      setPos(sube
        ? { bottom: window.innerHeight - r.top + 4, left: izquierda, width: ancho }
        : { top: r.bottom + 4, left: izquierda, width: ancho });
    };
    calcular();
    window.addEventListener('resize', calcular);
    window.addEventListener('scroll', calcular, true);
    return () => { window.removeEventListener('resize', calcular); window.removeEventListener('scroll', calcular, true); };
  }, [abierto]);

  useEffect(() => {
    if (abierto && pos) entrada.current?.focus();
  }, [abierto, pos !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!abierto) { setTexto(''); setActivo(0); setPos(null); }
  }, [abierto]);

  // Al escribir, la primera coincidencia queda activa.
  useEffect(() => { setActivo(0); }, [texto]);

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => {
      const dentro = (raiz.current && raiz.current.contains(e.target)) || (panel.current && panel.current.contains(e.target));
      if (!dentro) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  // La opción activa siempre a la vista, desplazando SOLO la lista (no la página).
  useEffect(() => {
    if (!abierto) return;
    const ul = lista.current;
    const li = document.getElementById(idOpcion(activo));
    if (!ul || !li) return;
    if (li.offsetTop < ul.scrollTop) ul.scrollTop = li.offsetTop;
    else if (li.offsetTop + li.offsetHeight > ul.scrollTop + ul.clientHeight) ul.scrollTop = li.offsetTop + li.offsetHeight - ul.clientHeight;
  }, [activo, abierto, texto]); // eslint-disable-line react-hooks/exhaustive-deps

  const elegir = (op) => {
    if (!op) return;
    setAbierto(false);
    boton.current?.focus();
    if (op.vacio) onChange('', null);
    else onChange(String(op.value), op);
  };

  const teclado = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActivo(i => Math.min(i + 1, filas.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActivo(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Home') { e.preventDefault(); setActivo(0); }
    else if (e.key === 'End') { e.preventDefault(); setActivo(Math.max(filas.length - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); elegir(filas[activo]); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setAbierto(false); boton.current?.focus(); }
    else if (e.key === 'Tab') setAbierto(false);
  };

  const tecladoBoton = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); if (!disabled) setAbierto(true); }
  };

  const textoBoton = value && elegida ? elegida.label : placeholder;
  const hayElegida = !!(value && elegida);

  return (
    <div className={`pcb${auto ? ` ${styles.auto}` : ''}`} ref={raiz} style={{ minWidth, maxWidth }}>
      <button
        type="button" ref={boton} disabled={disabled}
        className={`form-control pcb-trigger${triggerClassName ? ` ${triggerClassName}` : ''}`}
        aria-haspopup="listbox" aria-expanded={abierto}
        aria-label={`${ariaLabel}: ${hayElegida ? textoBoton : 'ninguna seleccionada'}`}
        onClick={() => setAbierto(a => !a)} onKeyDown={tecladoBoton}
      >
        <span className={hayElegida ? '' : 'pcb-placeholder'}>{textoBoton}</span>
        <i className="fas fa-chevron-down" aria-hidden="true" />
      </button>

      {abierto && pos && createPortal(
        <div className="pcb-panel" ref={panel} style={pos}>
          <input
            ref={entrada} type="text" className={`form-control pcb-search${conBusqueda ? '' : ` ${styles.entradaOculta}`}`}
            role="combobox" aria-expanded="true" aria-autocomplete="list"
            aria-controls={listId} aria-activedescendant={filas.length ? idOpcion(activo) : undefined}
            aria-label={`Buscar en ${ariaLabel.toLowerCase()}`} placeholder={searchPlaceholder}
            value={texto} maxLength={100} autoComplete="off"
            onChange={e => setTexto(e.target.value)} onKeyDown={teclado}
          />
          <ul id={listId} ref={lista} role="listbox" className="pcb-list" aria-label={ariaLabel}>
            {filas.map((op, i) => (
              <li
                key={op.vacio ? 'vacio' : op.value} id={idOpcion(i)} role="option" aria-selected={esElegida(op)}
                className={`pcb-opt${i === activo ? ' activo' : ''}${esElegida(op) ? ' elegido' : ''}${op.vacio ? ' vacio' : ''}`}
                onMouseEnter={() => setActivo(i)}
                onMouseDown={e => e.preventDefault()}
                onClick={() => elegir(op)}
              >
                <span className="pcb-nombre">{op.label}</span>
                {!op.vacio && op.sub && <span className="pcb-sub">{op.sub}</span>}
              </li>
            ))}
          </ul>
          {coinciden.length === 0 && (
            <div className="pcb-msg" role="status">{buscado ? `Sin resultados para «${texto.trim()}»` : 'No hay opciones'}</div>
          )}
          {coinciden.length > MAX_FILAS && (
            <div className="pcb-msg" role="status">Mostrando {MAX_FILAS} de {coinciden.length}. Escribe para afinar la búsqueda.</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
